/**
 * Shared Intersection Observer Hook
 * Replaces individual intersection observers with a single shared instance
 * 
 * Performance benefits:
 * - Single observer for all game cards
 * - Reduced memory usage
 * - Better performance with hundreds of cards
 */

import { useEffect, useRef, useState } from 'react';

interface ObserverEntry {
  element: Element;
  callback: (isVisible: boolean) => void;
}

class SharedIntersectionObserver {
  private observer: IntersectionObserver | null = null;
  private entries = new Map<Element, ObserverEntry>();
  private isInitialized = false;

  private init() {
    if (this.isInitialized || typeof window === 'undefined') return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const registeredEntry = this.entries.get(entry.target);
          if (registeredEntry) {
            registeredEntry.callback(entry.isIntersecting);
          }
        });
      },
      {
        rootMargin: '100px', // Reduced margin to be less aggressive during scroll restoration
        threshold: [0, 0.1], // Simplified thresholds to reduce calculations
      }
    );

    this.isInitialized = true;
  }

  public observe(element: Element, callback: (isVisible: boolean) => void) {
    if (!this.isInitialized) {
      this.init();
    }

    if (!this.observer) return;

    // Remove existing entry if any
    this.unobserve(element);

    // Add new entry
    this.entries.set(element, { element, callback });
    this.observer.observe(element);
  }

  public unobserve(element: Element) {
    if (this.observer && this.entries.has(element)) {
      this.observer.unobserve(element);
      this.entries.delete(element);
    }
  }

  public disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.entries.clear();
      this.isInitialized = false;
      this.observer = null;
    }
  }

  public getStats() {
    return {
      isInitialized: this.isInitialized,
      entriesCount: this.entries.size,
    };
  }
}

// Singleton instance
const sharedObserver = new SharedIntersectionObserver();

// Debug helper
if (process.env['NODE_ENV'] === 'development') {
  // @ts-expect-error fake error description to satisfy the type check gods
  window.sharedIntersectionObserver = sharedObserver;
}

/**
 * Hook to use shared intersection observer
 */
export const useSharedIntersectionObserver = (
  enabled: boolean = true,
  initialVisible: boolean = false
) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(initialVisible);

  useEffect(() => {
    const element = elementRef.current;
    if (!enabled || !element) return;

    // Register with shared observer
    sharedObserver.observe(element, (visible) => {
      setIsVisible(visible);
    });

    return () => {
      if (element) {
        sharedObserver.unobserve(element);
      }
    };
  }, [enabled, setIsVisible]);

  return {
    elementRef,
    isVisible,
    setIsVisible,
  };
};
